/**
 * AI Providers — per-endpoint LLM provider routing.
 *
 * Backs PlatformSettings.ai.providers (see backend/services/llmProvider.js).
 * Reva endpoints (reva.support, reva.business, reva.content) each map to a
 * provider + model. The "default" fallback applies when an endpoint isn't
 * explicitly configured.
 *
 * CSS prefix: aip- (AIProviders). The backend rejects writes that route
 * to a provider with no API key configured, so the UI also disables those
 * options to prevent the round-trip error.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import {
  Cpu,
  RefreshCw,
  Save,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import './AIProviders.css';

const ENDPOINT_LABELS = {
  'reva.support': {
    title: 'Reva — Support',
    sub: 'Help/setup answers via KB retrieval. Surfaced to every tier.',
  },
  'reva.business': {
    title: 'Reva — Business',
    sub: 'Predictions, forecasts, recommendations. PRO+ intent.',
  },
  'reva.content': {
    title: 'Reva — Content',
    sub: 'Generative content (descriptions, copy). PRO+ intent.',
  },
};

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
};

const AIProviders = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Server-supplied
  const [availability, setAvailability] = useState({ openai: false, anthropic: false });
  const [endpoints, setEndpoints] = useState([]);
  const [providerModels, setProviderModels] = useState({});
  const [knownProviders, setKnownProviders] = useState([]);

  // Editable
  const [defaultCfg, setDefaultCfg] = useState({ provider: 'openai', model: 'gpt-4o-mini' });
  const [endpointCfg, setEndpointCfg] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/ai/providers');
      if (!res.success) throw new Error(res.error || 'Failed to load');
      const { config, availability: av, endpoints: eps, providerModels: pm, knownProviders: kp } = res.data;
      setAvailability(av);
      setEndpoints(eps);
      setProviderModels(pm);
      setKnownProviders(kp);
      setDefaultCfg({ provider: config.default.provider, model: config.default.model });
      const seeded = {};
      eps.forEach((key) => {
        seeded[key] = config.endpoints?.[key] || { ...config.default };
      });
      setEndpointCfg(seeded);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // When provider changes, snap model to first available for that provider
  // — prevents an "openai + claude-haiku-4-5" invalid combo sitting in state.
  const updateEndpoint = (key, field, value) => {
    setSuccess('');
    setEndpointCfg((prev) => {
      const next = { ...prev[key], [field]: value };
      if (field === 'provider') {
        const firstModel = providerModels[value]?.[0];
        if (firstModel) next.model = firstModel;
      }
      return { ...prev, [key]: next };
    });
  };
  const updateDefault = (field, value) => {
    setSuccess('');
    setDefaultCfg((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'provider') {
        const firstModel = providerModels[value]?.[0];
        if (firstModel) next.model = firstModel;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = { default: defaultCfg, endpoints: endpointCfg };
      const res = await api.put('/admin/ai/providers', payload);
      if (!res.success) throw new Error(res.error || 'Save failed');
      setSuccess('Provider settings saved. New requests will use the updated routing.');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (label, sub, cfg, onChange) => {
    const modelOptions = providerModels[cfg.provider] || [];
    return (
      <div className="aip-row">
        <div className="aip-row-text">
          <div className="aip-row-title">{label}</div>
          {sub && <div className="aip-row-sub">{sub}</div>}
        </div>
        <div className="aip-row-controls">
          <label className="aip-field">
            <span className="aip-field-label">Provider</span>
            <div className="aip-select">
              <select value={cfg.provider} onChange={(e) => onChange('provider', e.target.value)}>
                {knownProviders.map((p) => (
                  <option key={p} value={p} disabled={!availability[p]}>
                    {PROVIDER_LABELS[p] || p}{availability[p] ? '' : ' (no key)'}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </div>
          </label>
          <label className="aip-field">
            <span className="aip-field-label">Model</span>
            <div className="aip-select">
              <select value={cfg.model} onChange={(e) => onChange('model', e.target.value)}>
                {!modelOptions.includes(cfg.model) && (
                  <option value={cfg.model}>{cfg.model} (custom)</option>
                )}
                {modelOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="aip-page">
      <header className="aip-header">
        <div className="aip-header-text">
          <h1 className="aip-title"><Cpu size={22} /><span>AI Providers</span></h1>
          <p className="aip-subtitle">
            Route each Reva endpoint to a provider + model. Changes take effect within ~60 seconds.
          </p>
        </div>
        <div className="aip-header-actions">
          <button type="button" className="aip-btn aip-btn-secondary" onClick={load} disabled={loading || saving}>
            <RefreshCw size={14} className={loading ? 'aip-spin' : ''} />
            <span>Reload</span>
          </button>
          <button type="button" className="aip-btn aip-btn-primary" onClick={save} disabled={loading || saving}>
            {saving ? <RefreshCw size={14} className="aip-spin" /> : <Save size={14} />}
            <span>{saving ? 'Saving…' : 'Save'}</span>
          </button>
        </div>
      </header>

      {error && (
        <div className="aip-banner aip-banner-error">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="aip-banner aip-banner-ok">
          <CheckCircle2 size={14} />
          <span>{success}</span>
        </div>
      )}

      <section className="aip-section">
        <h2 className="aip-section-title">Provider availability</h2>
        <div className="aip-chip-row">
          {knownProviders.map((p) => (
            <span key={p} className={`aip-chip ${availability[p] ? 'aip-chip-on' : 'aip-chip-off'}`}>
              {availability[p] ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              <span>{PROVIDER_LABELS[p] || p}</span>
              <span className="aip-chip-sub">{availability[p] ? 'configured' : 'no API key'}</span>
            </span>
          ))}
        </div>
        <p className="aip-help">
          Add an API key in the backend <code>.env</code> to enable a provider; missing-key providers are disabled in the dropdowns below.
        </p>
      </section>

      {loading ? (
        <div className="aip-loading">
          <RefreshCw size={14} className="aip-spin" />
          <span>Loading…</span>
        </div>
      ) : (
        <>
          <section className="aip-section">
            <h2 className="aip-section-title">Default fallback</h2>
            <p className="aip-help">Used when an endpoint below has no explicit override.</p>
            <div className="aip-rows">
              {renderRow('Default', 'Catch-all for any endpoint not listed below.', defaultCfg, updateDefault)}
            </div>
          </section>

          <section className="aip-section">
            <h2 className="aip-section-title"><Sparkles size={14} /> Per-endpoint routing</h2>
            <div className="aip-rows">
              {endpoints.map((key) => {
                const meta = ENDPOINT_LABELS[key] || { title: key, sub: '' };
                return (
                  <React.Fragment key={key}>
                    {renderRow(
                      meta.title,
                      meta.sub,
                      endpointCfg[key] || defaultCfg,
                      (field, value) => updateEndpoint(key, field, value),
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AIProviders;
